<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-configuration-summary-generation-prompt">
    <div class="header">
      <div class="title">
        Summary Generation Prompt
        <el-tooltip v-if="tooltipText" effect="dark" popper-class="wy-page-tooltip-popup" placement="bottom">
          <div slot="content"><span v-html="$t(tooltipText)"></span></div>
          <div class="info-icon"></div>
        </el-tooltip>
      </div>
      <el-button @click="handleUpdateCompanyPromptSettings" :disabled="!isContentHasChanged" class="update-btn">Update</el-button>
    </div>

    <el-tabs v-model="activeTab" class="prompt-tabs">
      <el-tab-pane label="VA Engaged, Live Agent Connected" name="BotToLCPrompt">
        <div class="tab-action">
          <span @click="handleClearPrompt('BotToLC')" class="link">Clear prompt</span>
          <span @click="handleRevertPromptToOriginal('BotToLC')" class="link">Discard unsaved changes</span>
        </div>
        <el-input
          v-loading="isUpdateLoading"
          v-model="botToLCPrompt"
          :autosize="{ minRows: rowsCount, maxRows: rowsCount }"
          type="textarea"
          placeholder="Please provide a prompt to generate summaries for conversations involving both virtual and live agents."
          @input="botToLCPromptIsDirty = true"
        />
      </el-tab-pane>

      <el-tab-pane label="VA Contained only" name="BotOnlyContainedPrompt">
        <div class="tab-action">
          <span @click="handleClearPrompt('BotOnlyContained')" class="link">Clear prompt</span>
          <span @click="() => handleRevertPromptToOriginal('BotOnlyContained')" :class="{ link: botOnlyContainedPromptIsDirty, 'link-disabled': !botOnlyContainedPromptIsDirty }"
            >Discard unsaved changes</span
          >
        </div>
        <el-input
          v-loading="isUpdateLoading"
          v-model="botOnlyContainedPrompt"
          :autosize="{ minRows: rowsCount, maxRows: rowsCount }"
          type="textarea"
          placeholder="Please provide a prompt to generate summaries for conversations involving virtual agents only."
          @input="botOnlyContainedPromptIsDirty = true"
        />
      </el-tab-pane>

      <el-tab-pane label="Live Agent only" name="LCOnlyPrompt">
        <div class="tab-action">
          <span @click="handleClearPrompt('LCOnly')" class="link">Clear prompt</span>
          <span @click="() => handleRevertPromptToOriginal('LCOnly')" :class="{ link: lCOnlyPromptIsDirty, 'link-disabled': !lCOnlyPromptIsDirty }">Discard unsaved changes</span>
        </div>
        <el-input
          v-loading="isUpdateLoading"
          v-model="lCOnlyPrompt"
          :autosize="{ minRows: rowsCount, maxRows: rowsCount }"
          type="textarea"
          placeholder="Please provide a prompt to generate summaries for conversations involving live agents only."
          @input="lCOnlyPromptIsDirty = true"
        />
      </el-tab-pane>

      <el-tab-pane label="Live Agent Requested, not Connected" name="BotOnlyEscalatedPrompt">
        <div class="tab-action">
          <span @click="handleClearPrompt('BotOnlyEscalated')" class="link">Clear prompt</span>
          <span @click="() => handleRevertPromptToOriginal('BotOnlyEscalated')" :class="{ link: botOnlyEscalatedPromptIsDirty, 'link-disabled': !botOnlyEscalatedPromptIsDirty }"
            >Discard unsaved changes</span
          >
        </div>
        <el-input
          v-loading="isUpdateLoading"
          v-model="botOnlyEscalatedPrompt"
          :autosize="{ minRows: rowsCount, maxRows: rowsCount }"
          type="textarea"
          placeholder="Please provide a prompt to generate summaries for conversations where live agent handoff was requested but an agent was not connected."
          @input="botOnlyEscalatedPromptIsDirty = true"
        />
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';

export default Vue.extend({
  name: 'wy-analytics-configuration-summary-generation-prompt',
  title: 'Configuration',
  props: ['controller'],
  data() {
    return {
      tooltipText: 'Configure prompts used to generate transcript summaries, depending on different conversation types.',
      rowsCount: 20,
      activeTab: 'BotToLCPrompt',

      botToLCPrompt: '',
      botToLCPromptIsDirty: false,
      botToLCPromptOrigin: '',

      botOnlyContainedPrompt: '',
      botOnlyContainedPromptIsDirty: false,
      botOnlyContainedPromptOrigin: '',

      lCOnlyPrompt: '',
      lCOnlyPromptIsDirty: false,
      lCOnlyPromptOrigin: '',

      botOnlyEscalatedPrompt: '',
      botOnlyEscalatedPromptIsDirty: false,
      botOnlyEscalatedPromptOrigin: '',

      isUpdateButtonDisabled: true,
      isUpdateLoading: false,
    };
  },

  computed: {
    ...mapGetters(['authUser', 'companyProfilePrompts']),
    isDirty() {
      return this.botToLCPromptIsDirty || this.botOnlyContainedPromptIsDirty || this.lCOnlyPromptIsDirty || this.botOnlyEscalatedPromptIsDirty || this.isUpdateLoading;
    },

    isContentHasChanged() {
      return this.botToLCPrompt != this.botToLCPromptOrigin || this.botOnlyContainedPromptOrigin != this.botOnlyContainedPrompt 
      || this.lCOnlyPromptOrigin != this.lCOnlyPrompt || this.botOnlyEscalatedPromptOrigin != this.botOnlyEscalatedPrompt;
    }
  },

  methods: {
    ...mapActions(['fetchCompanyPromptSettings', 'updateCompanyPromptSettings']),

    async fetchAndLoadPrompts() {
      await this.fetchCompanyPromptSettings(this.authUser);
      if(!this.companyProfilePrompts.prompts || !this.companyProfilePrompts.prompts.home) {
        this.companyProfilePrompts.prompts = {
          home: {
            BotToLC: '',
            BotOnlyContained: '',
            LCOnly: '',
            BotOnlyEscalated: '',
          }
        }
      }
      const prompts = this.companyProfilePrompts.prompts.home;

      this.botToLCPrompt = prompts.BotToLC;
      this.botToLCPromptOrigin = this.botToLCPrompt;

      this.botOnlyContainedPrompt = prompts.BotOnlyContained;
      this.botOnlyContainedPromptOrigin = this.botOnlyContainedPrompt

      this.lCOnlyPrompt = prompts.LCOnly;
      this.lCOnlyPromptOrigin = this.lCOnlyPrompt;


      this.botOnlyEscalatedPrompt = prompts.BotOnlyEscalated;
      this.botOnlyEscalatedPromptOrigin = this.botOnlyEscalatedPrompt;
    },

    resetData() {
      this.isUpdateLoading = false;
      this.isUpdateButtonDisabled = true;

      this.botToLCPromptIsDirty = false;
      this.botOnlyContainedPromptIsDirty = false;
      this.lCOnlyPromptIsDirty = false;
      this.botOnlyEscalatedPromptIsDirty = false;
    },

    handleClearPrompt(promptName) {
      const promptFields = {
        BotToLC: 'botToLCPrompt',
        BotOnlyContained: 'botOnlyContainedPrompt',
        LCOnly: 'lCOnlyPrompt',
        BotOnlyEscalated: 'botOnlyEscalatedPrompt',
      };

      if (!(promptName in promptFields)) {
        throw new Error(`Invalid tab name: ${promptName}`);
      }

      const field = promptFields[promptName];
      this[field] = '';
      this[`${field}IsDirty`] = true;

      this.controller.hasNoSavedChanges = false;
    },

    handleRevertPromptToOriginal(promptName) {
      const promptFields = {
        BotToLC: 'botToLCPrompt',
        BotOnlyContained: 'botOnlyContainedPrompt',
        LCOnly: 'lCOnlyPrompt',
        BotOnlyEscalated: 'botOnlyEscalatedPrompt',
      };

      if (!(promptName in promptFields)) {
        throw new Error(`Invalid tab name: ${promptName}`);
      }

      const field = promptFields[promptName];
      const originalValue = this.companyProfilePrompts.prompts.home[promptName];
      this[field] = originalValue;
      this[`${field}IsDirty`] = false;
    },

    async handleUpdateCompanyPromptSettings() {
      this.isUpdateLoading = true;
      try {
        // const updateStatus = 404;
        const updateStatus = await this.updateCompanyPromptSettings({
          prompts: {
            home: {
              BotToLC: this.botToLCPrompt,
              BotOnlyEscalated: this.botOnlyEscalatedPrompt,
              BotOnlyContained: this.botOnlyContainedPrompt,
              LCOnly: this.lCOnlyPrompt,
            },
          },
          updatedBy: this.authUser.user.email,
        });
        if (updateStatus === 200) {
          this.$message({
            message: 'Prompt successfully updated.',
            showClose: true,
            type: 'success',
            customClass: 'configuration-success-message',
          });
          this.botToLCPromptOrigin = this.botToLCPrompt;
          this.botOnlyContainedPromptOrigin = this.botOnlyContainedPrompt
          this.lCOnlyPromptOrigin = this.lCOnlyPrompt;
          this.botOnlyEscalatedPromptOrigin = this.botOnlyEscalatedPrompt;
        } else if(updateStatus) {
          this.$message({
            message: 'Prompt failed to update.',
            showClose: true,
            type: 'error',
            customClass: 'configuration-failed-message prompt',
          });
          this.isUpdateButtonDisabled = false;
        }
        else {
          this.isUpdateButtonDisabled = false;
        }
      } catch (error) {
        console.error(error);
        this.controller.hasError = true;
        this.isUpdateButtonDisabled = false;
      }

      this.resetData();
    },

    beforeLeave(newTab) {
      if (this.controller.hasNoSavedChanges) {
        this.handleSaveUnchanges(newTab);
        return false;
      }
    },

    handleSaveUnchanges(newTab, handleAppModalClose, fromMainTab) {
      this.controller.$refs.updateDialog.title = `Review changes`;
      this.controller.$refs.updateDialog.content = 'If you leave this page, any changes you have made will be lost. Do you want to save the changes you made?';
      this.controller.$refs.updateDialog.open();
      if (typeof this.controller.hasError != 'undefined' && !this.controller.hasError) {
        this.controller.hasError = document.querySelectorAll('.has-error').length > 0;
      }
      if (this.controller.hasError) {
        this.controller.$refs.updateDialog.disableSaveBtn = true;
      } else {
        this.controller.$refs.updateDialog.disableSaveBtn = false;
      }

      /* Confirm Btn */
      this.controller.$refs.updateDialog.confirmButtonCallback = async () => {
        /* SAVE METHOD */
        this.handleUpdateCompanyPromptSettings();
        /* SAVE METHOD */

        this.controller.hasNoSavedChanges = false;

        if (fromMainTab) {
          if (handleAppModalClose) {
            handleAppModalClose();
          } else {
            this.controller.handleTabClick({ name: newTab });
            this.controller.activeTab = newTab;
          }
        } else {
          if (handleAppModalClose) {
            handleAppModalClose();
          } else {
            this.activeName = newTab;
          }
        }
      };

      /* Discard Btn */
      this.controller.$refs.updateDialog.discardButtonCallback = async () => {
        /* DISCARD METHOD */
        this.fetchAndLoadPrompts();
        this.resetData();
        /* DISCARD METHOD */

        this.controller.hasNoSavedChanges = false;

        if (fromMainTab) {
          if (handleAppModalClose) {
            handleAppModalClose();
          } else {
            this.controller.handleTabClick({ name: newTab });
            this.controller.activeTab = newTab;
          }
        } else {
          if (handleAppModalClose) {
            handleAppModalClose();
          } else {
            this.activeName = newTab;
          }
        }
      };
    },
  },

  watch: {
    isDirty: {
      handler(val) {
        this.isUpdateButtonDisabled = !val;
      },
      immediate: true,
    },
    isUpdateButtonDisabled: {
      handler(val) {
        this.controller.hasNoSavedChanges = !val;
      },
      immediate: true,
    },
  },

  async created() {
    await this.fetchAndLoadPrompts();
  },
});
</script>

<style lang="scss">
.wy-analytics-configuration-summary-generation-prompt {
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      font-size: 16px;
      font-weight: 600;

      display: flex;
      gap: 8px;
    }
    .update-btn {
      width: fit-content;
      height: fit-content;
      padding: 8px 16px 8px 16px;
      font-size: 15px;
      cursor: pointer;
      background: var(--site-button-primary-btn-background-primary-color);
      color: var(--site-button-primary-btn-text-primary-color) !important;
      border: 1px solid var(--site-button-primary-btn-hover-border-primary-color);
      font-family: Helvetica Neue, Helvetica, Arial, sans-serif;
      border-radius: 5px;
      // margin-right: 15px;
      align-items: center;
      justify-content: center;
    }
    .update-btn:hover {
      border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
      background-color: var(--site-button-primary-btn-hover-background-primary-color) !important;
      border-radius: 5px;
      color: var(--site-button-primary-btn-text-primary-color) !important;
    }
    .update-btn:active {
      background-color: var(--site-button-primary-btn-pressed-background-primary-color) !important;
    }
    .update-btn:focus {
      border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
      border-radius: 5px;
      color: var(--site-button-primary-btn-text-primary-color) !important;
    }
    .update-btn.is-disabled {
      background: var(--site-button-primary-btn-disabled-background-primary-color);
      cursor: not-allowed;
    }
    .update-btn.is-disabled:hover {
      border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
      background: var(--site-button-primary-btn-disabled-background-primary-color) !important;
      border-radius: 5px;
      color: var(--site-button-primary-btn-text-primary-color) !important;
    }
    .update-btn.is-disabled:active {
      background: var(--site-button-primary-btn-disabled-background-primary-color) !important;
    }
    .update-btn.is-disabled:focus {
      border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
      border-radius: 5px;
      color: var(--site-button-primary-btn-text-primary-color) !important;
    }
  }

  .info-icon {
    background-image: url('../../../assets/icons/icon_info.svg');
    background-repeat: no-repeat;
    background-size: 70%;
    height: 24px;
    width: 24px;
    margin-top: 2px;
  }

  .prompt-tabs {
    margin-top: 18px;
  }

  .el-tabs__item.is-active {
    background-color: transparent !important;
    border-bottom: 2px solid #106fbc;
  }

  .el-tabs__item {
    font-weight: 400;
    font-size: 14px;
  }

  .prompt-tabs > .el-tabs__content {
    padding: 6px 0px;
    font-size: 12px;
    // display: block;
  }

  .el-textarea__inner {
    // styling of text input body
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    color: var(--site-font-chart-color);
    padding: 24px;
    border-radius: 0px;
    cursor: pointer;
    height: calc(100vh - 369px) !important;
    min-height: unset !important;
    resize: none;
    // on hover
    &:hover {
      background-color: #f4f6f8;
    }
  }

  .tab-action {
    display: flex;
    justify-content: flex-end;
    gap: 18px;
    color: #1a55ad;
    margin-bottom: 20px;

    .link {
      cursor: pointer;
      font-weight: 500;
    }

    .link-disabled {
      cursor: not-allowed;
      font-weight: 500;
      color: gray;
    }
  }

  .wy-toast-success {
    background-color: #86d2ae;
  }
}
.configuration-failed-message.prompt {
  top: 20px !important;
  .el-message__closeBtn {
    color: #5C5F62;
  }
}
</style>
